import { watch } from 'vue';
import type { FileManagerMountsState } from './useFileManagerMounts';
import type { FileManagerNavigationState } from './useFileManagerNavigation';

/**
 * Redirects a stale/unresolvable deep-linked path back to root — the case a renamed project or a
 * removed library leaves behind in someone's bookmark.
 *
 * No readiness gate: the mount table arrives already settled (D4), so "this path maps to no mount" is
 * always the real answer rather than possibly "the answer has not arrived yet". `mounts` is still a
 * watch source, because the host is free to hand down a new table (a refetch, a probe that hid a
 * library) and a path that was fine a moment ago then is not.
 *
 * A fetch error on a resolvable path (e.g. a since-deleted folder) is deliberately out of scope — that
 * already surfaces via FileExplorerView's own error panel, from which the sidebar/breadcrumb still
 * work.
 *
 * The dependency is deliberately narrowed to `isNavigablePath` rather than the whole mounts state.
 * "Is this path a place" is the ONLY question this guard gets to ask, and the narrowing is what makes
 * that structural instead of a matter of discipline: the neighbouring predicates answer a strictly
 * weaker question (see isNavigablePath's docstring for the rule and for the regression that came of
 * asking one of them here), and without them in scope that mistake is not expressible in this file.
 * That is the anti-recurrence measure — the fix on its own would have been just as easy to undo.
 */
export const useNavigationPathGuard = (
  navigation: Pick<FileManagerNavigationState, 'currentPath' | 'navigateTo'>,
  mounts: Pick<FileManagerMountsState, 'mounts' | 'isNavigablePath'>,
) => {
  watch(
    [navigation.currentPath, mounts.mounts],
    ([path]) => {
      // No root early return: the root is navigable under both table shapes — synthetic when no mount
      // sits at it, drive-backed when one does (collapseSoleMount) — so isNavigablePath already covers
      // it, and a second answer here could only ever diverge from that one.
      if (mounts.isNavigablePath(path)) return;
      navigation.navigateTo('/', { replace: true });
    },
    { immediate: true },
  );
};
